import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import api from '../../services/api';
import { getCountries, getStates, getCities, normalizeCountryForForm } from '../../data/locationData';
import { getPhoneCode } from '../../data/phoneCodes';
import { getTimezone, getCountryFromTimezone, getAvailableTimezones, timezoneNames } from '../../data/timezones';
import { getLeadAgeGroupSelectOptions } from '../Leads/leadDisplayUtils';
import { ageToAgeGroup } from '../../constants/ageGroups';
import {
  splitStoredName,
  joinNameParts,
  formatParentContactLabel,
  resolveParentContactNames,
  inferContactPerson,
} from '../../utils/personName';
import BrandLogo from '../../components/BrandLogo';
import { calculateAgeFromCalendarYmd, toCalendarYmd } from '../../utils/dateTimeUtils';
import ParentConsentSection, { PARENT_CONSENT_VERSION } from './ParentConsentSection';

const initialForm = {
  email: '',
  student_name: '',
  age: '',
  date_of_birth: '',
  gender: '',
  class_grade: '',
  school_name: '',
  school_address: '',
  kid_hobby: '',
  father_first_name: '',
  father_last_name: '',
  mother_first_name: '',
  mother_last_name: '',
  contact_person: '',
  parent_first_name: '',
  parent_last_name: '',
  phone: '',
  phone_code: '+91',
  country: '',
  state: '',
  city: '',
  address: '',
  postal_code: '',
  timezone: 'UTC',
  age_group: '',
  student_type: '',
  class_type_preference: '',
  parent_fb_id: '',
  parent_linkedin_id: '',
};

function computeAgeFromDob(dobStr) {
  const age = calculateAgeFromCalendarYmd(dobStr);
  return age == null ? '' : String(age);
}

function normalizePersonName(value) {
  return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function signerNameFromForm(form) {
  if (!form.contact_person) return '';
  const names = resolveParentContactNames(form);
  return joinNameParts(names.parent_first_name, names.parent_last_name);
}

function consentBlockerMessage(consent) {
  if (consent.mediaUse !== 'yes' && consent.mediaUse !== 'no') {
    return 'Please tick one option for screenshots and videos.';
  }
  if (!consent.agreed) return 'Please tick the agreement box.';
  if (consent.signature.trim().length < 2) return 'Please type your full name.';
  return '';
}

function EnrollHeader({ showLogo = true }) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 3,
        px: 2,
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {showLogo && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <BrandLogo height={72} />
        </Box>
      )}
      <Typography variant="h4" fontWeight={600} color="primary.main" gutterBottom>
        Enrollment form
      </Typography>
      <Typography variant="subtitle1" color="text.secondary">
        Enrollment form – Students
      </Typography>
    </Box>
  );
}

export default function Enroll() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(!!token);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submittedSummary, setSubmittedSummary] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [availableStates, setAvailableStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [consent, setConsent] = useState({
    mediaUse: '',
    agreed: false,
    signature: '',
  });
  const consentRef = useRef(null);

  const ageGroupOptions = getLeadAgeGroupSelectOptions(form.age_group);

  useEffect(() => {
    if (!token) {
      setError('Missing enrollment link. Please use the link sent to you.');
      setLoading(false);
      return;
    }
    api.get('/api/public/enroll', { params: { token } })
      .then((res) => {
        if (res.data?.success && res.data?.preFill) {
          const preFill = res.data.preFill;
          const country = normalizeCountryForForm(preFill.country || '');
          const phoneCode = preFill.phone_code || getPhoneCode(country);
          const fatherParts = splitStoredName(preFill.father_name);
          const motherParts = splitStoredName(preFill.mother_name);
          const merged = {
            ...initialForm,
            ...preFill,
            father_first_name: preFill.father_first_name ?? fatherParts.first,
            father_last_name: preFill.father_last_name ?? fatherParts.last,
            mother_first_name: preFill.mother_first_name ?? motherParts.first,
            mother_last_name: preFill.mother_last_name ?? motherParts.last,
            country,
            phone_code: phoneCode,
          };
          delete merged.father_name;
          delete merged.mother_name;
          if (merged.date_of_birth) {
            merged.date_of_birth = toCalendarYmd(merged.date_of_birth);
            merged.age = computeAgeFromDob(merged.date_of_birth);
            const group = ageToAgeGroup(merged.age);
            if (group && !merged.age_group) merged.age_group = group;
          }
          merged.contact_person = inferContactPerson(merged);
          setForm(merged);
          if (country) {
            setAvailableStates(getStates(country));
            if (preFill.state) {
              setAvailableCities(getCities(country, preFill.state));
            }
          }
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.message || err.message || 'Invalid or expired link';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleChange = (field) => (e) => {
    const value = e.target?.value ?? '';
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'date_of_birth') {
        next.age = computeAgeFromDob(value);
        const group = ageToAgeGroup(next.age);
        if (group) next.age_group = group;
      }
      if (field === 'timezone') {
        const countryFromTz = getCountryFromTimezone(value);
        if (countryFromTz) {
          next.phone_code = getPhoneCode(countryFromTz);
        }
      }
      return next;
    });
    if (fieldErrors[field]) setFieldErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleCountryChange = (e) => {
    const country = e.target.value;
    const states = getStates(country);
    const phoneCode = getPhoneCode(country);
    const timezoneValue = getTimezone(country);
    setAvailableStates(states);
    setAvailableCities([]);
    setForm((prev) => ({
      ...prev,
      country,
      state: '',
      city: '',
      phone_code: phoneCode,
      timezone: timezoneValue,
    }));
    setFieldErrors((prev) => ({ ...prev, country: '', state: '', city: '' }));
  };

  const handleStateChange = (e) => {
    const state = e.target.value;
    if (state === form.state) return;
    const cities = getCities(form.country, state);
    const timezoneValue = getTimezone(form.country, state);
    setAvailableCities(cities);
    setForm((prev) => ({
      ...prev,
      state,
      city: '',
      timezone: timezoneValue,
    }));
    setFieldErrors((prev) => ({ ...prev, state: '', city: '' }));
  };

  const validate = () => {
    const err = {};
    if (!form.email?.trim()) err.email = 'Required';
    if (!form.student_name?.trim()) err.student_name = 'Required';
    if (!form.date_of_birth?.trim()) err.date_of_birth = 'Required';
    else if (!computeAgeFromDob(form.date_of_birth)) err.date_of_birth = 'Please enter a valid date of birth';
    if (!form.gender) err.gender = 'Required';
    if (!form.class_grade?.trim()) err.class_grade = 'Required';
    if (!form.school_name?.trim()) err.school_name = 'Required';
    if (!form.school_address?.trim()) err.school_address = 'Required';
    if (!form.kid_hobby?.trim()) err.kid_hobby = 'Required';
    if (!form.father_first_name?.trim()) err.father_first_name = 'Required';
    if (!form.father_last_name?.trim()) err.father_last_name = 'Required';
    if (!form.mother_first_name?.trim()) err.mother_first_name = 'Required';
    if (!form.mother_last_name?.trim()) err.mother_last_name = 'Required';
    if (!form.contact_person) err.contact_person = 'Please select who is filling this form';
    if (form.contact_person === 'guardian') {
      if (!form.parent_first_name?.trim()) err.parent_first_name = 'Required';
      if (!form.parent_last_name?.trim()) err.parent_last_name = 'Required';
    }
    if (!form.country?.trim()) err.country = 'Required';
    if (!form.address?.trim()) err.address = 'Required';
    if (!form.state?.trim()) err.state = 'Required';
    if (!form.city?.trim()) err.city = 'Required';
    if (!form.postal_code?.trim()) err.postal_code = 'Required';
    if (!form.phone?.trim()) err.phone = 'Required';
    if (!form.timezone?.trim()) err.timezone = 'Required';
    if (consent.mediaUse !== 'yes' && consent.mediaUse !== 'no') {
      err.media_use_consent = 'Please tick one option for screenshots and videos.';
    }
    if (!consent.agreed) err.agreement_accepted = 'Please tick this box to continue.';
    const signerName = signerNameFromForm(form);
    if (!consent.signature.trim()) {
      err.electronic_signature = 'Please type your full name.';
    } else if (!signerName) {
      err.electronic_signature = 'Complete the parent details above, then type that full name.';
    } else if (normalizePersonName(consent.signature) !== normalizePersonName(signerName)) {
      err.electronic_signature = 'Type the same full name shown above.';
    }
    setFieldErrors(err);
    const consentErrorKeys = [
      'media_use_consent',
      'agreement_accepted',
      'electronic_signature',
    ];
    const hasFormError = Object.keys(err).some((key) => !consentErrorKeys.includes(key));
    if (hasFormError) {
      document.getElementById('enrollment-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (consentErrorKeys.some((key) => err[key])) {
      consentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    return Object.keys(err).length === 0;
  };

  const handleConsentChange = (field, value) => {
    setConsent((prev) => ({ ...prev, [field]: value }));
    const errorKey = {
      mediaUse: 'media_use_consent',
      agreed: 'agreement_accepted',
      signature: 'electronic_signature',
    }[field];
    if (errorKey && fieldErrors[errorKey]) {
      setFieldErrors((prev) => ({ ...prev, [errorKey]: '' }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;
    setSubmitLoading(true);
    const father_name = joinNameParts(form.father_first_name, form.father_last_name);
    const mother_name = joinNameParts(form.mother_first_name, form.mother_last_name);
    const { parent_first_name, parent_last_name } = resolveParentContactNames(form);
    const {
      contact_person,
      father_first_name,
      father_last_name,
      mother_first_name,
      mother_last_name,
      ...formPayload
    } = form;
    api.post('/api/public/enroll', {
      token,
      ...formPayload,
      father_name,
      mother_name,
      father_first_name: form.father_first_name,
      father_last_name: form.father_last_name,
      mother_first_name: form.mother_first_name,
      mother_last_name: form.mother_last_name,
      contact_person: form.contact_person,
      parent_first_name,
      parent_last_name,
      consent_version: PARENT_CONSENT_VERSION,
      media_use_consent: consent.mediaUse === 'yes',
      agreement_accepted: true,
      electronic_signature: consent.signature.trim(),
    })
      .then((res) => {
        if (res.data?.success) {
          setSubmittedSummary({
            studentName: form.student_name,
            fatherName: father_name,
            motherName: mother_name,
            contactLabel: formatParentContactLabel({
              parent_first_name,
              parent_last_name,
              father_name,
              mother_name,
            }),
          });
          setSuccess(true);
        } else {
          setError(res.data?.message || 'Something went wrong');
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.message || 'Failed to submit. Please try again.';
        const details = err.response?.data?.errors;
        if (details && Array.isArray(details)) {
          const byField = {};
          details.forEach((d) => {
            if (d.path?.[0]) byField[d.path[0]] = d.message || 'Invalid';
          });
          setFieldErrors((prev) => ({ ...prev, ...byField }));
        }
        setError(msg);
      })
      .finally(() => setSubmitLoading(false));
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'grey.50',
        }}
      >
        <EnrollHeader />
        <CircularProgress sx={{ mt: 4 }} />
      </Box>
    );
  }

  if (!token || (error && !submitLoading && !success)) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: 'grey.50', py: 4 }}>
        <EnrollHeader />
        <Container maxWidth="sm">
          <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
            <Alert severity="error">{error || 'Invalid enrollment link'}</Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: 'grey.50', py: 4 }}>
        <EnrollHeader />
        <Container maxWidth="sm">
          <Paper elevation={2} sx={{ p: 4, mt: 2, textAlign: 'center' }}>
            <Alert severity="success" sx={{ py: 2, textAlign: 'left' }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ textAlign: 'center' }}>
                Enrollment details saved successfully.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                You are now enrolled as a student. We will be in touch shortly.
              </Typography>
              {submittedSummary ? (
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="body2"><strong>Student:</strong> {submittedSummary.studentName}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Father:</strong> {submittedSummary.fatherName}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Mother:</strong> {submittedSummary.motherName}
                  </Typography>
                  {submittedSummary.contactLabel ? (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      <strong>Primary contact:</strong> {submittedSummary.contactLabel}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}
            </Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  const SectionTitle = ({ children }) => (
    <Typography variant="h6" color="primary.main" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
      {children}
    </Typography>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'grey.50', pb: 6 }}>
      <EnrollHeader />
      <Container maxWidth="md" sx={{ mt: 2 }}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 4 },
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Please fill in the details below to enroll your child for the Bacchapanti course. Fields marked with * are required.
          </Typography>

          <form id="enrollment-form" onSubmit={handleSubmit}>
            <SectionTitle>Student details</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email *"
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  error={!!fieldErrors.email}
                  helperText={fieldErrors.email}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Student name *"
                  value={form.student_name}
                  onChange={handleChange('student_name')}
                  error={!!fieldErrors.student_name}
                  helperText={fieldErrors.student_name}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of birth *"
                  type="date"
                  value={form.date_of_birth}
                  onChange={handleChange('date_of_birth')}
                  InputLabelProps={{ shrink: true }}
                  error={!!fieldErrors.date_of_birth}
                  helperText={fieldErrors.date_of_birth}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Age"
                  value={form.age}
                  variant="outlined"
                  size="small"
                  disabled
                  helperText="Auto-calculated from date of birth"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset" error={!!fieldErrors.gender} size="small">
                  <FormLabel component="legend" sx={{ fontSize: '0.875rem' }}>Gender *</FormLabel>
                  <RadioGroup row value={form.gender} onChange={handleChange('gender')}>
                    <FormControlLabel value="Male" control={<Radio size="small" />} label="Male" />
                    <FormControlLabel value="Female" control={<Radio size="small" />} label="Female" />
                  </RadioGroup>
                  {fieldErrors.gender && (
                    <Typography variant="caption" color="error">{fieldErrors.gender}</Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Age Group</InputLabel>
                  <Select
                    value={form.age_group}
                    onChange={handleChange('age_group')}
                    label="Age Group"
                  >
                    <MenuItem value=""><em>Not specified</em></MenuItem>
                    {ageGroupOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Student Type</InputLabel>
                  <Select
                    value={form.student_type}
                    onChange={handleChange('student_type')}
                    label="Student Type"
                  >
                    <MenuItem value=""><em>Not specified</em></MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="special_needs">Special Needs</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Class Type Preference</InputLabel>
                  <Select
                    value={form.class_type_preference}
                    onChange={handleChange('class_type_preference')}
                    label="Class Type Preference"
                  >
                    <MenuItem value=""><em>Not specified</em></MenuItem>
                    <MenuItem value="one_on_one">One-on-One</MenuItem>
                    <MenuItem value="group">Group</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <SectionTitle>School information</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Class / Grade *"
                  value={form.class_grade}
                  onChange={handleChange('class_grade')}
                  error={!!fieldErrors.class_grade}
                  helperText={fieldErrors.class_grade}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="School name *"
                  value={form.school_name}
                  onChange={handleChange('school_name')}
                  error={!!fieldErrors.school_name}
                  helperText={fieldErrors.school_name}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="School address *"
                  value={form.school_address}
                  onChange={handleChange('school_address')}
                  error={!!fieldErrors.school_address}
                  helperText={fieldErrors.school_address}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Child's hobby / interest *"
                  value={form.kid_hobby}
                  onChange={handleChange('kid_hobby')}
                  error={!!fieldErrors.kid_hobby}
                  helperText={fieldErrors.kid_hobby}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <SectionTitle>Parent / Guardian</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Father's first name *"
                  value={form.father_first_name}
                  onChange={handleChange('father_first_name')}
                  error={!!fieldErrors.father_first_name}
                  helperText={fieldErrors.father_first_name}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Father's last name *"
                  value={form.father_last_name}
                  onChange={handleChange('father_last_name')}
                  error={!!fieldErrors.father_last_name}
                  helperText={fieldErrors.father_last_name}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mother's first name *"
                  value={form.mother_first_name}
                  onChange={handleChange('mother_first_name')}
                  error={!!fieldErrors.mother_first_name}
                  helperText={fieldErrors.mother_first_name}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mother's last name *"
                  value={form.mother_last_name}
                  onChange={handleChange('mother_last_name')}
                  error={!!fieldErrors.mother_last_name}
                  helperText={fieldErrors.mother_last_name}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl error={!!fieldErrors.contact_person} component="fieldset">
                  <FormLabel component="legend">Who is filling this form? *</FormLabel>
                  <RadioGroup
                    row
                    value={form.contact_person}
                    onChange={handleChange('contact_person')}
                  >
                    <FormControlLabel value="father" control={<Radio size="small" />} label="Father" />
                    <FormControlLabel value="mother" control={<Radio size="small" />} label="Mother" />
                    <FormControlLabel
                      value="guardian"
                      control={<Radio size="small" />}
                      label="Other guardian"
                    />
                  </RadioGroup>
                  {fieldErrors.contact_person ? (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {fieldErrors.contact_person}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Contact details below will be saved under this person&apos;s name.
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              {form.contact_person === 'guardian' ? (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Guardian first name *"
                      value={form.parent_first_name}
                      onChange={handleChange('parent_first_name')}
                      error={!!fieldErrors.parent_first_name}
                      helperText={fieldErrors.parent_first_name}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Guardian last name *"
                      value={form.parent_last_name}
                      onChange={handleChange('parent_last_name')}
                      error={!!fieldErrors.parent_last_name}
                      helperText={fieldErrors.parent_last_name}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                </>
              ) : null}
            </Grid>

            <Divider sx={{ my: 2 }} />
            <SectionTitle>Location & contact</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small" required error={!!fieldErrors.country}>
                  <InputLabel>Country *</InputLabel>
                  <Select
                    value={form.country}
                    onChange={handleCountryChange}
                    label="Country *"
                  >
                    {getCountries().map((country) => (
                      <MenuItem key={country} value={country}>{country}</MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.country && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {fieldErrors.country}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Shipping address (to help in dispatching blindfold kit) *
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  All of these fields are required so we can send the blindfold kit.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Full Street Address (House/Apt#, if any)"
                  multiline
                  rows={2}
                  value={form.address}
                  onChange={handleChange('address')}
                  error={!!fieldErrors.address}
                  helperText={fieldErrors.address}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  options={availableStates}
                  inputValue={form.state}
                  disabled={!form.country}
                  onInputChange={(_, value) => handleStateChange({ target: { value: value || '' } })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      size="small"
                      label="State"
                      error={!!fieldErrors.state}
                      helperText={fieldErrors.state || (!form.country ? 'Select country first' : '')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  options={availableCities}
                  inputValue={form.city}
                  disabled={!form.state}
                  onInputChange={(_, value) => handleChange('city')({ target: { value: value || '' } })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      size="small"
                      label="City"
                      error={!!fieldErrors.city}
                      helperText={fieldErrors.city || (!form.state ? 'Select state first' : '')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  required
                  label="Zipcode"
                  value={form.postal_code}
                  onChange={handleChange('postal_code')}
                  error={!!fieldErrors.postal_code}
                  helperText={fieldErrors.postal_code}
                  variant="outlined"
                  size="small"
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required error={!!fieldErrors.timezone}>
                  <InputLabel>Timezone *</InputLabel>
                  <Select
                    value={form.timezone}
                    onChange={handleChange('timezone')}
                    label="Timezone *"
                  >
                    {getAvailableTimezones().map((tz) => (
                      <MenuItem key={tz} value={tz}>{timezoneNames[tz] || tz}</MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.timezone && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {fieldErrors.timezone}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone number *"
                  value={form.phone}
                  onChange={handleChange('phone')}
                  error={!!fieldErrors.phone}
                  helperText={fieldErrors.phone || 'Country code is set from selected country'}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">{form.phone_code}</InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <SectionTitle>Optional</SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Parent's Facebook ID"
                  value={form.parent_fb_id}
                  onChange={handleChange('parent_fb_id')}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Parent's LinkedIn ID"
                  value={form.parent_linkedin_id}
                  onChange={handleChange('parent_linkedin_id')}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </Grid>

            <ParentConsentSection
              sectionRef={consentRef}
              signerName={signerNameFromForm(form)}
              childName={form.student_name}
              mediaUse={consent.mediaUse}
              agreed={consent.agreed}
              signature={consent.signature}
              errors={fieldErrors}
              onChange={handleConsentChange}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {consentBlockerMessage(consent) ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, textAlign: 'center' }}>
                  {consentBlockerMessage(consent)}
                </Typography>
              ) : null}
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitLoading || Boolean(consentBlockerMessage(consent))}
                sx={{ minWidth: 180, py: 1.5 }}
              >
                {submitLoading ? <CircularProgress size={26} color="inherit" /> : 'Submit enrollment'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}
